import { Job } from "@core/domains/jobs";
import {
  CursorPaginationSchema,
  apiEndpoint,
  cursorPaginatedResponse,
  internalApi,
} from "@core/ports/http";
import { z } from "@core/zod";

import { API_DOMAIN } from "@platform/core";

import { MEDIA_SERVICE } from "../config/index.ts";
import { MediaAsset } from "../models/index.ts";

export const MediaAssetPreviewSchema = z.object(
  { mediaAsset: MediaAsset.schema, previewUrl: z.url() },
  {
    mediaAsset: "The media asset",
    previewUrl: "The URL of the asset's preview, or of its original",
  },
);

export interface MediaAssetPreview
  extends z.infer<typeof MediaAssetPreviewSchema> {}

export const mediaInternalApi = internalApi(
  { domain: API_DOMAIN, path: `${MEDIA_SERVICE.LABEL}/int` },
  {
    backfillPreviews: apiEndpoint({
      response: z.object(
        { job: Job.schema },
        { job: "The backfill job started for the missing previews" },
      ),
    }),
    createUploadUrl: apiEndpoint({
      body: MediaAsset.createSchema.omit({
        hasPreview: true,
        metadataPrincipalId: true,
      }),
      response: z.object(
        { mediaAsset: MediaAsset.schema, uploadUrl: z.url() },
        {
          mediaAsset: "The media asset created for the upload",
          uploadUrl: "The presigned URL to PUT the file's bytes to",
        },
      ),
    }),
    finalizeUpload: apiEndpoint({
      body: z.object(
        { id: MediaAsset.shape.id, principalId: MediaAsset.shape.principalId },
        {
          id: MediaAsset.descriptions.id,
          principalId: MediaAsset.descriptions.principalId,
        },
      ),
      response: z.object(
        { mediaAsset: MediaAsset.schema },
        { mediaAsset: "The media asset, marked uploaded" },
      ),
    }),
    getAssets: apiEndpoint({
      body: z.object(
        {
          ...CursorPaginationSchema.shape,
          principalId: MediaAsset.shape.principalId,
        },
        {
          ...CursorPaginationSchema.descriptions,
          principalId: MediaAsset.descriptions.principalId,
        },
      ),
      response: cursorPaginatedResponse(MediaAsset.schema),
    }),
    getDownloadUrl: apiEndpoint({
      body: z.object(
        { id: MediaAsset.shape.id, principalId: MediaAsset.shape.principalId },
        {
          id: MediaAsset.descriptions.id,
          principalId: MediaAsset.descriptions.principalId,
        },
      ),
      response: z.object(
        { downloadUrl: z.url() },
        { downloadUrl: "The URL to download the media asset's file from" },
      ),
    }),
    getPreviews: apiEndpoint({
      body: z.object(
        {
          ...CursorPaginationSchema.shape,
          principalId: MediaAsset.shape.principalId,
        },
        {
          ...CursorPaginationSchema.descriptions,
          principalId: MediaAsset.descriptions.principalId,
        },
      ),
      response: cursorPaginatedResponse(MediaAssetPreviewSchema),
    }),
    getPreviewsByIds: apiEndpoint({
      body: z.object(
        {
          ids: z.array(MediaAsset.shape.id),
          principalId: MediaAsset.shape.principalId,
        },
        {
          ids: "The ids of the media assets to preview",
          principalId: MediaAsset.descriptions.principalId,
        },
      ),
      response: z.object(
        { previews: z.array(MediaAssetPreviewSchema) },
        { previews: "The previews of the media assets" },
      ),
    }),
  },
);
